import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';

const API_BASE_URL = "https://moncopieur.cloud-erp.ma";

interface Partner {
  id: number;
  name: string;
  email: string;
  [key: string]: any;
}

interface AuthContextType {
  isAuthenticated: boolean;
  partner: Partner | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  logout: () => Promise<void>;
  checkToken: () => Promise<boolean>;
  isLoading: boolean;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  company_type: 'person' | 'company';
  street?: string;
  city?: string;
  zip?: string;
  country_id?: number;
  state_id?: number;
  phone?: string;
  mobile?: string;
  vat?: string;
  company_registry?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Load auth state from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedPartner = localStorage.getItem('auth_partner');
    const storedExpiresAt = localStorage.getItem('auth_expires_at');

    if (storedToken && storedPartner && storedExpiresAt) {
      const expiresAt = new Date(storedExpiresAt);
      if (expiresAt > new Date()) {
        setToken(storedToken);
        setPartner(JSON.parse(storedPartner));
        // Check token validity with server
        checkTokenValidity(storedToken);
      } else {
        clearAuthData();
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const checkTokenValidity = async (tokenToCheck: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ws/check_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: tokenToCheck }),
      });

      const data = await response.json();

      if (data.success && data.valid) {
        setIsAuthenticated(true);
        if (data.partner) {
          setPartner(data.partner);
          localStorage.setItem('auth_partner', JSON.stringify(data.partner));
        }
      } else {
        clearAuthData();
        if (data.reason) {
          toast({
            title: "Session expired",
            description: data.reason,
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('Token check failed:', error);
      clearAuthData();
    } finally {
      setIsLoading(false);
    }
  };

  const clearAuthData = () => {
    setIsAuthenticated(false);
    setPartner(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_partner');
    localStorage.removeItem('auth_expires_at');
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ws/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        setToken(data.token);
        setPartner(data.partner);
        setIsAuthenticated(true);

        // Store in localStorage
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_partner', JSON.stringify(data.partner));
        localStorage.setItem('auth_expires_at', data.expires_at);

        toast({
          title: "Connexion réussie",
          description: `Bienvenue ${data.partner?.name || 'back'}!`,
        });

        return true;
      } else {
        toast({
          title: "Échec de la connexion",
          description: "Email ou mot de passe incorrect",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast({
        title: "Erreur de connexion",
        description: "Impossible de se connecter au serveur",
        variant: "destructive",
      });
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/ws/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });
      } catch (error) {
        // Ignore logout errors to stay idempotent
        console.warn('Logout request failed:', error);
      }
    }

    clearAuthData();
    toast({
      title: "Déconnexion réussie",
      description: "Vous avez été déconnecté avec succès",
    });
  };

  const register = async (data: RegisterData): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/ws/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (result.success && result.token) {
        setToken(result.token);
        setPartner(result.partner);
        setIsAuthenticated(true);

        // Store in localStorage
        localStorage.setItem('auth_token', result.token);
        localStorage.setItem('auth_partner', JSON.stringify(result.partner));
        localStorage.setItem('auth_expires_at', result.expires_at);

        toast({
          title: "Inscription réussie",
          description: `Bienvenue ${result.partner?.name}!`,
        });

        return true;
      } else {
        toast({
          title: "Échec de l'inscription",
          description: result.error || "Une erreur est survenue",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error('Registration failed:', error);
      toast({
        title: "Erreur d'inscription",
        description: "Impossible de se connecter au serveur",
        variant: "destructive",
      });
      return false;
    }
  };

  const checkToken = async (): Promise<boolean> => {
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/ws/check_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (data.success && data.valid) {
        return true;
      } else {
        clearAuthData();
        return false;
      }
    } catch (error) {
      console.error('Token check failed:', error);
      clearAuthData();
      return false;
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    partner,
    token,
    login,
    register,
    logout,
    checkToken,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};